/**
 * @file date-picker/use-date-picker-state.ts
 *
 * Core state hook for DatePicker — hoisted state pattern.
 * Equivalent to `rememberDatePickerState()` in Android Compose Material3.
 *
 * @example
 * ```tsx
 * const state = useDatePickerState({
 *   locale: { locale: 'en-US', weekStartsOn: 1 },
 *   selectableDates: {
 *     isSelectableDate: (ms) => new Date(ms).getUTCDay() !== 0,
 *     isSelectableYear: (year) => year >= 2020,
 *   }
 * });
 *
 * <DatePicker state={state} />
 * <p>Selected: {state.selectedDateMs}</p>
 * ```
 */

import { useCallback, useMemo, useState } from "react";
import {
	getNavigableMonth,
	isMonthNavigable,
	normalizeToMonthStart,
	resolveLocale,
	startOfDayUTC,
	todayUTC,
} from "../shared/calendar-model";
import type { DisplayMode } from "../shared/types";
import { ALL_DATES_SELECTABLE } from "../shared/types";
import type {
	DatePickerState,
	UseDatePickerStateOptions,
} from "./date-picker.types";

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_YEAR_RANGE: [number, number] = [1900, 2100];
const DEFAULT_DISPLAY_MODE: DisplayMode = "picker";

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook for managing state of DatePicker.
 *
 * @param options - Initial state configuration
 * @returns `DatePickerState` object with state and actions
 */
export const useDatePickerState = (
	options?: UseDatePickerStateOptions,
): DatePickerState => {
	// ── Initial values ──────────────────────────────────────────────────────────
	const initialSelectedMs = options?.initialSelectedDateMs ?? null;

	const initialMonthMs = normalizeToMonthStart(
		options?.initialDisplayedMonthMs ??
			(initialSelectedMs !== null ? initialSelectedMs : todayUTC()),
	);

	const yearRange = options?.yearRange ?? DEFAULT_YEAR_RANGE;
	const selectableDates = options?.selectableDates ?? ALL_DATES_SELECTABLE;
	const initialDisplayMode =
		options?.initialDisplayMode ?? DEFAULT_DISPLAY_MODE;
	const locale = resolveLocale(options?.locale);

	// ── State ───────────────────────────────────────────────────────────────────
	const [selectedDateMs, setSelectedDateMs] = useState<number | null>(
		initialSelectedMs !== null ? startOfDayUTC(initialSelectedMs) : null,
	);
	const [displayedMonthMs, setDisplayedMonthMs] =
		useState<number>(initialMonthMs);
	const [displayMode, setDisplayModeState] =
		useState<DisplayMode>(initialDisplayMode);

	// ── Actions ─────────────────────────────────────────────────────────────────

	const selectDate = useCallback(
		(ms: number | null): void => {
			if (ms === null) {
				setSelectedDateMs(null);
				return;
			}
			const normalized = startOfDayUTC(ms);
			// Validate selectable
			if (!selectableDates.isSelectableDate(normalized)) return;
			setSelectedDateMs(normalized);
			// Auto-navigate calendar to month of selected date
			setDisplayedMonthMs(normalizeToMonthStart(normalized));
		},
		[selectableDates],
	);

	const navigateMonthAction = useCallback(
		(direction: "prev" | "next"): void => {
			setDisplayedMonthMs(
				(prev) =>
					getNavigableMonth(prev, direction, yearRange, selectableDates) ??
					prev,
			);
		},
		[yearRange, selectableDates],
	);

	const navigateToMonth = useCallback(
		(year: number, month: number): void => {
			if (!isMonthNavigable(year, yearRange, selectableDates)) return;
			setDisplayedMonthMs(Date.UTC(year, month, 1));
		},
		[yearRange, selectableDates],
	);

	const setDisplayMode = useCallback((mode: DisplayMode): void => {
		setDisplayModeState(mode);
	}, []);

	const reset = useCallback((): void => {
		setSelectedDateMs(
			initialSelectedMs !== null ? startOfDayUTC(initialSelectedMs) : null,
		);
		setDisplayedMonthMs(initialMonthMs);
		setDisplayModeState(initialDisplayMode);
	}, [initialSelectedMs, initialMonthMs, initialDisplayMode]);

	// ── Memoized state object ───────────────────────────────────────────────────
	return useMemo<DatePickerState>(
		() => ({
			selectedDateMs,
			displayedMonthMs,
			displayMode,
			yearRange,
			locale,
			selectableDates,
			selectDate,
			navigateMonth: navigateMonthAction,
			navigateToMonth,
			setDisplayMode,
			reset,
		}),
		[
			selectedDateMs,
			displayedMonthMs,
			displayMode,
			yearRange,
			locale,
			selectableDates,
			selectDate,
			navigateMonthAction,
			navigateToMonth,
			setDisplayMode,
			reset,
		],
	);
};
